import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {
	Button,
	Checkbox,
	ComboBox,
	DatePicker,
	EMPHASIS,
	FlexRow,
	Icon,
	Label,
	Text,
	TextArea,
	TextField,
	tokensForThemeProvider,
	WorkSignifier,
} from '@forecast-it/design-system';
import Util from '../../../../shared/util/util';
import AssignedDropdown from '../../../../shared/components/dropdowns/assigned-dropdown/assigned_dropdown';
import {LabelDropdown} from '../../../../../components/dropdowns/LabelDropdown/LabelDropdown';
import {HoursInput} from '../../../../shared/components/inputs/hours-input/hours_input_view';
import UpdateTaskSetMutation from '../../../../../mutations/update_task_set_mutation';
import {useForecastFetchQuery} from '../../../../shared/hooks/useForecastFetchQuery';
import {showTaskSetScopeModal} from '../../../../../containers/modal/task-set/TaskSetScopeModal';
import {EndsCandidate, showTaskSetEndsModal} from '../../../../../containers/modal/task-set/TaskSetEndsModal';
import {createToast} from '../../../../shared/components/toasts/toast';
import {InstanceState} from './taskSetReach';
import {describePattern, formatSetDate, isCountShortfall, TaskSetShortfall} from './describeTaskSet';
import {expandRecurrence, TaskSetFormState} from './useTaskSetPreview';
import {BODY_PADDING_Y} from './TaskSetDrawer';
import {graphql} from 'react-relay';

const NAME_MAX_LENGTH = 191;

/** The code the extend mutation fails with when its new tasks could not be created. */
const EXTEND_FAILED_CODE = 'TASK_SET_EXTEND_FAILED';

/** The one option in the Ends list that names no instance, because none exists yet. */
const LATER = '__LATER__';
/* The single option the Ends field carries until the preflight lists the real
   ones. Its own id so it can never be confused with an instance's sequence. */
const PENDING = '__PENDING__';

/* Far enough ahead to contain the next occurrence of every pattern the set can
   have, yearly included, and near enough that the expander is not walked to its
   probe limit to find one date. */
const addYear = (iso: string): string => `${Number(iso.slice(0, 4)) + 1}${iso.slice(4)}`;

/** The next calendar day, crossing month and year boundaries. ISO in, ISO out. */
const nextDay = (iso: string): string => {
	const date = new Date(`${iso}T00:00:00Z`);
	date.setUTCDate(date.getUTCDate() + 1);
	return date.toISOString().slice(0, 10);
};

const Row = styled.div`
	display: flex;
	/* Matches the canvas and the create drawer's own Row. Two drawers on one
	   surface with different gutters is a difference the eye catches without
	   being able to name. */
	gap: 12px;
	margin-bottom: 14px;

	> * {
		flex: 1 1 0;
		min-width: 0;
	}
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

/**
 * Assignees are drawn as avatars, and the control supplies its own grey
 * uppercase label which this form does not use -- it labels the field itself so
 * every row reads alike. The avatars are positioned absolutely at an offset that
 * assumes that label is present, so without the correction below they sit low in
 * the input and clip at its bottom edge.
 *
 * The avatar rules are inert on the role-only call, which draws none.
 *
 * Lifted from the create drawer's AssignedField, which found all three the hard
 * way. Change one, change the other.
 */
const AssignedField = styled(Field)`
	overflow: visible;

	/* The control is flex: 1 1 0 inside this flex column, so it GROWS to whatever
	   height the row is -- and the row is as tall as its tallest field. Sharing a
	   row with Phase, whose note wraps to two lines, stretched it to 86px against
	   its neighbour's 44px and left its caret stranded halfway down the empty
	   space. Pinning it to its natural height puts the slack below the control,
	   where the other fields keep theirs. */
	&& .assigned-dropdown-wrapper {
		flex: 0 0 44px;
		height: 44px;
	}

	&& .assigned-dropdown-wrapper .assigned-icons {
		top: 7px;
	}

	/* The avatar is a 13.28px block with a 6.64px CSS-border triangle above and
	   below it, so it needs 26.56px, and the box it sits in is 26px and hides its
	   overflow -- half a pixel off the bottom point, which reads as a blunt
	   hexagon. The box clips for name truncation, and the name is hidden here, so
	   letting it show costs nothing. */
	&& .assigned-dropdown-wrapper .assigned-icons .forecast-person {
		overflow: visible;
	}

	/* The surrounding content sets line-height: 24px, and the avatar's initials
	   carry none of their own, so the text box inherits 24px while the hexagon's
	   middle band is 13.28px. Anchored to the top of that band, the letters land
	   about 5px low and the triangle drawn beneath paints over them -- the
	   initials are sheared off flat. Everywhere else in the app this avatar
	   inherits line-height: normal, so that is what it gets back. */
	&& .person-initials-hexagon-shape .initials {
		line-height: normal;
	}
`;

const Hint = styled.div`
	margin-top: 4px;
	font-size: 12px;
	line-height: 17px;
	color: #7d7d7d;
`;

/** A set that stopped early: what was asked, what exists, and the way to close the gap. */
const ShortfallNotice = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
	margin-top: 4px;
	font-size: 12px;
	line-height: 17px;
	color: ${tokensForThemeProvider.sky.sys.color.text.content.main};
`;

/** Icon beside the notice text, matching the list's own shortfall flag. */
const ShortfallNoticeText = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 6px;
`;

/**
 * The notice at the top. It states the two limits that decide everything below,
 * so nobody has to discover them by having a change quietly not apply.
 */
const Notice = styled.div`
	margin-bottom: 18px;
	padding: 10px 12px;
	border-radius: 4px;
	background: #f4f6fb;
	font-size: 12px;
	line-height: 18px;
	color: rgba(0, 0, 0, 0.87);
`;

/**
 * A disabled control, not a disabled field: the label and hint sit outside it
 * exactly as they do on the live control beside it, so the row reads as two
 * fields of one kind with one of them switched off -- rather than as a grey slab
 * next to a form. Shown rather than hidden, because hiding the pattern would read
 * as the feature not existing at all.
 *
 * Sized to the design system's own input so the two controls in the row line up.
 */
/**
 * Holds the read-only labels at the same height as the picker it stands in for,
 * so the row does not shift between a viewer who may edit and one who may not.
 */
const ReadOnlyLabels = styled.div`
	display: flex;
	align-items: center;
	min-height: 40px;
`;

const LockedValue = styled.div`
	display: flex;
	align-items: center;
	min-height: 40px;
	padding: 0 12px;
	border: 1px solid #ededed;
	border-radius: 4px;
	background: #fafafa;
	font-size: 13px;
	color: rgba(0, 0, 0, 0.6);
`;

/**
 * The form is one child of the drawer's flex-column body, so it has to be a
 * growing flex column itself or the action row's `margin-top: auto` has no
 * context to push against and the buttons ride up under the fields on a short
 * form.
 */
const FormRoot = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding-top: 14px;
	border-top: 1px solid #e6e6e6;
	/* margin-top pushes the row to the foot of the panel when the form is short
	   enough to leave room; sticky keeps it on screen when it is not. The
	   background is needed because the form now scrolls underneath it. */
	position: sticky;
	bottom: -${BODY_PADDING_Y}px;
	margin-top: auto;
	background: #ffffff;
	/* A sticky row inside a padded scroller pins to the scroller's CONTENT box,
	   so it comes to rest one bottom-padding short of the visible edge and the
	   form scrolls through the strip underneath it -- which read as the buttons
	   not sticking at all. The negative offset moves the pin down onto the
	   visible edge, the padding fills the strip with the row's own background,
	   and the negative margin keeps the taller box from adding that height back
	   to the scroll length. Same three-part fix the create drawer's footer
	   needed; change one, check the other. */
	padding-bottom: ${BODY_PADDING_Y}px;
	margin-bottom: -${BODY_PADDING_Y}px;
`;

interface Option {
	readonly id: string;
	readonly name?: string | null;
}

export interface EditableTaskSet {
	readonly id: string;
	readonly name?: string | null;
	readonly instanceName?: string | null;
	readonly description?: string | null;
	readonly estimateForecast?: number | null;
	readonly billable?: boolean | null;
	readonly role?: {readonly id: string} | null;
	readonly phase?: {readonly id: string} | null;
	readonly frequency?: string | null;
	readonly intervalLength?: number | null;
	readonly weekdays?: readonly (string | null)[] | null;
	readonly monthlyWeekday?: string | null;
	readonly monthlyMode?: string | null;
	readonly dayOfMonth?: number | null;
	readonly weekOrdinal?: number | null;
	readonly boundType?: string | null;
	readonly boundCount?: number | null;
	readonly boundEndDate?: string | null;
	/** How far generation actually got. The only place a count-bounded set's last date exists. */
	readonly materialisedTo?: string | null;
	/** Where the pattern is counted from. expandRecurrence re-walks the rule from it. */
	readonly seedDate?: string | null;
	/** The instance the set ends after, already loaded with the set, so the Ends
	 *  field can name it before the preflight that lists every option arrives. */
	readonly lastInstance?: {readonly companyTaskId?: number | null; readonly startDate?: string | null} | null;
	/** Present only when generation stopped before the requested end. */
	readonly generationShortfall?: TaskSetShortfall | null;
	readonly defaultLabels?: readonly {readonly id: string}[] | null;
	readonly defaultAssignedPersons?: readonly {readonly id: string}[] | null;
}

export interface TaskSetEditFormProps {
	set: EditableTaskSet;
	projectId: string;
	isPointsProject?: boolean;
	roles?: ReadonlyArray<Option>;
	assignablePersons?: ReadonlyArray<{id: string; firstName?: string | null; lastName?: string | null}>;
	phases?: ReadonlyArray<Option>;
	labels?: ReadonlyArray<{
		id: string;
		name?: string | null;
		color?: string | null;
		category?: {id: string; name?: string | null} | null;
	}>;
	/** Hides the billing field where the project tracks no revenue. */
	showBillable?: boolean;
	/**
	 * Drives the revenue row in the Ends confirmation. Absent where the host does
	 * not hold one, which hides that row rather than printing a bare number with
	 * no unit.
	 */
	currencySymbol?: string | null;
	/**
	 * Draws the same form with every control switched off and no Save. A reader
	 * who cannot edit tasks gets this rather than no way in at all: view, create
	 * and edit are one layout, and the fields simply do not take input. The
	 * server refuses the write regardless, so nothing here is the guard.
	 */
	readOnly?: boolean;
	onCancel: () => void;
	onSaved: (instancesUpdated: number) => void;
}

/**
 * What an edit would reach, read on Save so the scope chooser can carry real
 * counts. Fetched then rather than on open: the reader may cancel, and the
 * answer moves while the form is up.
 */
const editPreflightQuery = graphql`
	query TaskSetEditFormPreflightQuery($id: ID!) {
		node(id: $id) {
			... on TaskSet {
				id
				editPreflight {
					untouched
					instanceCount
					instances {
						sequence
						companyTaskId
						eligible
						worked
						invoiced
						hasActuals
					}
				}
			}
		}
	}
`;

/**
 * How far back this viewer may bring the end, and what each instance would cost
 * to lose.
 *
 * <p>Read when the reader first touches the Ends field rather than on open: most
 * edits never touch it, and the floor has to be on screen WHILE they pick a date
 * rather than only after they have committed to one.
 *
 * <p>The instance's own `estimateForecastPrice` rather than an estimate
 * multiplied by a rate looked up here -- two routes to one number is two answers
 * to one question the moment either drifts.
 */
const endsPreflightQuery = graphql`
	query TaskSetEditFormEndsPreflightQuery($id: ID!) {
		node(id: $id) {
			... on TaskSet {
				id
				endsPreflight {
					currentEndDate
					floorSequence
					defaultSequence
					canShorten
					instances {
						sequence
						companyTaskId
						startDate
						blocked
						hasActuals
						estimateForecast
						task {
							estimateForecastPrice
						}
					}
				}
			}
		}
	}
`;

/** One instance as the ends preflight reports it, plus what pins the floor. */
interface EndsInstance extends EndsCandidate {
	readonly blocked?: boolean | null;
	readonly companyTaskId?: number | null;
	readonly hasActuals?: boolean | null;
}

interface EndsPreflight {
	readonly currentEndDate?: string | null;
	readonly floorSequence?: number | null;
	readonly canShorten?: boolean | null;
	readonly instances?: ReadonlyArray<EndsInstance> | null;
}

const sameIds = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean =>
	a.length === b.length && a.every(id => b.includes(id));

/**
 * The set drawer's edit mode.
 *
 * <p>Edits the SET's stored defaults, not one instance. Which instances the
 * change then reaches is the server's decision, asked for on Save and confirmed
 * by the scope chooser; this form never claims a reach of its own.
 *
 * <p>Ends is editable and carries its own confirmation, because moving it
 * destroys or creates instances rather than rewriting attributes on work that
 * stays -- a different class of change from everything else here, and one the
 * scope chooser has nothing to say about.
 *
 * <p>Pattern is shown disabled with its reason rather than hidden. Changing how
 * often a set repeats decides which dates exist at all, and matching existing
 * instances to new dates is its own problem; hiding the field would read as the
 * feature not existing.
 *
 * <p>A set created before the defaults columns existed returns null for every
 * stored value. The form renders that as empty rather than borrowing one
 * instance's values, which would show a number nobody set on the set.
 */
export const TaskSetEditForm = ({
	set,
	projectId,
	isPointsProject,
	roles = [],
	assignablePersons = [],
	phases = [],
	labels = [],
	showBillable,
	currencySymbol,
	readOnly = false,
	onCancel,
	onSaved,
}: TaskSetEditFormProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;

	const initialLabelIds = (set.defaultLabels || []).map(label => label.id);
	const initialPersonIds = (set.defaultAssignedPersons || []).map(person => person.id);

	const [setName, setSetName] = useState(set.name || '');
	const [instanceName, setInstanceName] = useState(set.instanceName || '');
	const [description, setDescription] = useState(set.description || '');
	/* What the stored number MEANS depends on the project: minutes on an hours
	   project, a point count on a points one -- exactly as the create drawer
	   writes it. Converting unconditionally showed a 5-point set as 0.083 and
	   pushed 180 to every eligible instance when someone typed 3. */
	const [estimateValue, setEstimateValue] = useState<number | null>(
		set.estimateForecast == null ? null : isPointsProject ? set.estimateForecast : set.estimateForecast / 60
	);
	const [roleId, setRoleId] = useState<string | null>(set.role?.id ?? null);
	const [phaseId, setPhaseId] = useState<string | null>(set.phase?.id ?? null);
	const [assignedPersonIds, setAssignedPersonIds] = useState<string[]>(initialPersonIds);
	const [labelIds, setLabelIds] = useState<string[]>(initialLabelIds);
	// Only used by the read-only chips. Resolved from the project's labels rather
	// than stored on the set, which holds ids alone.
	const selectedLabels = labels.filter(label => labelIds.includes(label.id));
	// Null means the set says nothing, which is not false: an untouched box leaves
	// the phase's own default in force.
	const [nonBillable, setNonBillable] = useState(set.billable === false);
	/* Where the set ends TODAY. A date-bounded set stores it; a count-bounded one
	   does not -- the count is its bound -- so its end is the generation
	   watermark, which is where its last instance actually falls. Editing Ends on
	   a count-bounded set converts it to a date bound, which is the same thing the
	   backend does and is why the field is one control for both.

	   A set whose generation stopped early starts from where it really got, not
	   from the date it asked for: the field names the instance the set ends after,
	   and opening on the request would claim instances that do not exist. */
	const shortfall = set.generationShortfall ?? null;
	const initialEnd =
		(shortfall ? set.materialisedTo : set.boundType === 'DATE' ? set.boundEndDate : set.materialisedTo) ?? '';
	const [boundEndDate, setBoundEndDate] = useState(initialEnd);
	/* Whether the reader has deliberately chosen an Ends value, as against the
	   field simply opening on it. On a short set, the value it opens with IS the
	   current last task -- so picking that same option must still count as a
	   choice, or Save can never be enabled to accept the set as it stands. */
	const [endsTouched, setEndsTouched] = useState(false);
	const [saving, setSaving] = useState(false);

	const {fetch: fetchPreflight, data: preflightData} = useForecastFetchQuery<
		{
			node?: {
				id?: string | null;
				editPreflight?: {
					untouched?: boolean | null;
					instances?: ReadonlyArray<InstanceState> | null;
				} | null;
			} | null;
		},
		{id: string}
	>(editPreflightQuery, true);

	// True only between pressing Save and the preflight landing. Without it a
	// preflight fetched for an earlier Save that the reader cancelled would open
	// the chooser again on the next render.
	const awaitingPreflight = useRef(false);

	const {fetch: fetchEndsPreflight, data: endsData} = useForecastFetchQuery<
		{node?: {id?: string | null; endsPreflight?: EndsPreflight | null} | null},
		{id: string}
	>(endsPreflightQuery, true);
	const endsPreflight = endsData?.node?.endsPreflight ?? null;
	/* Fetched when the form opens rather than on first touch. The Ends field's own
	   VALUE names an instance -- "After T707, 17 Dec 2026" -- and the set fragment
	   carries no instances, so there is nothing to render until this lands. Only
	   ever on a form someone deliberately opened. */
	const endsRequested = useRef(false);
	useEffect(() => {
		/* Never for a reader who cannot change the end. The preflight walks every
		   instance for delete permission, locked periods and invoiced time, and
		   resolves each one's task and price -- so opening a 500-instance set just
		   to look at it loaded 500 tasks through the financial resolver. A reader
		   needs the field's VALUE, which the set already carries, not the list of
		   dates they are not being offered. */
		if (readOnly || endsRequested.current) return;
		endsRequested.current = true;
		fetchEndsPreflight({id: set.id});
		// Deps are deliberately partial: fetchEndsPreflight is rebuilt every render,
		// so listing it would re-fire this on each one.
	}, [set.id, readOnly]);

	/**
	 * Re-reads the instances after a save that changed them.
	 *
	 * <p>The drawer keeps this form MOUNTED so a trip to the list does not cost
	 * what the reader typed, so "fetch once on mount" also meant "never fetch
	 * again" -- every later Ends decision was judged against the set as it was
	 * before the first save. That is not only wrong in the options and the counts:
	 * whether a new date SHORTENS the set is decided from these instances, so a
	 * stale list could read a shortening as an extension and destroy instances
	 * with no confirmation at all.
	 */
	const refetchEnds = () => {
		endsRequested.current = true;
		fetchEndsPreflight({id: set.id});
	};

	const storedEstimate = estimateValue == null ? null : isPointsProject ? estimateValue : Math.round(estimateValue * 60);
	const initialNonBillable = set.billable === false;

	/**
	 * The fields the reader actually changed, in the server's vocabulary.
	 *
	 * <p>Only these are sent. A form that submitted its whole shape would push
	 * the nulls a pre-migration set holds onto every instance the edit reaches and
	 * blank work nobody touched.
	 */
	const changedFields: string[] = [];
	if (setName !== (set.name || '')) changedFields.push('setName');
	if (instanceName !== (set.instanceName || '')) changedFields.push('name');
	if (description !== (set.description || '')) changedFields.push('description');
	if (storedEstimate !== (set.estimateForecast ?? null)) changedFields.push('estimate');
	if (roleId !== (set.role?.id ?? null)) changedFields.push('role');
	if (phaseId !== (set.phase?.id ?? null)) changedFields.push('phase');
	if (!sameIds(assignedPersonIds, initialPersonIds)) changedFields.push('assigned');
	if (!sameIds(labelIds, initialLabelIds)) changedFields.push('labels');
	if (nonBillable !== initialNonBillable) changedFields.push('billable');

	/* The subset that reaches the instances. The set's own name is stored on the
	   set and propagates to nothing -- the instances carry `name`, which is a
	   different field -- so renaming only the set used to open the scope chooser
	   offering to apply it to N instances, and then report that 0 were updated. */
	const instanceChangedFields = changedFields.filter(field => field !== 'setName');

	/* Blocked only when the reader EMPTIED a name, not whenever one is empty.
	   A set created before the defaults columns existed stores no instance name,
	   so the field renders empty and is never sent -- requiring it to be filled
	   made Save permanently dead on exactly those sets, which is most of them. */
	const blankedAName =
		(changedFields.includes('setName') && setName.trim().length === 0) ||
		(changedFields.includes('name') && instanceName.trim().length === 0);

	/* Ends is deliberately NOT in changedFields. Everything in that list rewrites
	   an attribute on work that stays and is scoped by the chooser; moving the end
	   destroys or creates the work itself and is scoped by nothing. Folding it in
	   would put "Ends" in a sentence about which instances take a change.

	   A short set opens with boundEndDate already equal to initialEnd -- both are
	   the last task that actually exists -- so a plain inequality can never be
	   true there, and Save stayed disabled however the reader chose to resolve
	   it. endsTouched covers exactly that: picking the current last task on a
	   short set is still a deliberate choice, even though it leaves the value
	   unchanged. */
	const endsChanged = boundEndDate !== '' && (boundEndDate !== initialEnd || (!!shortfall && endsTouched));

	const endsInstances: ReadonlyArray<EndsInstance> = endsPreflight?.instances ?? [];
	/* The earliest date this viewer may end the set on, which is the start date of
	   the LAST instance they may not delete. One blocked instance late in a series
	   pins the whole field however clean the ones before it are. Null until the
	   preflight lands, which is also the only state where the field cannot say
	   whether a date is reachable. */
	const floorDate = endsInstances.find(instance => instance.sequence === endsPreflight?.floorSequence)?.startDate ?? null;
	// Shortening at all is refused, so the field says why instead of offering
	// options the server will reject.
	const cannotShorten = !!endsPreflight && endsPreflight.canShorten === false;
	/* The LATEST start date, not the last instance by sequence. The server destroys
	   every instance that starts after the chosen date whatever its position, and an
	   instance dragged past the final one makes those two different answers: a date
	   between them read as an extension here, so no confirmation appeared, and the
	   server then deleted that instance with its time, comments and files. */
	const lastStart = endsInstances.reduce<string | null>(
		(latest, instance) =>
			instance.startDate && (latest === null || instance.startDate > latest) ? instance.startDate : latest,
		null
	);
	const shortening = endsChanged && !!lastStart && boundEndDate < lastStart;
	const belowFloor = shortening && ((cannotShorten && !!lastStart) || (!!floorDate && boundEndDate < floorDate));

	/* The instance the set runs TO, which is what the field names -- "After T707,
	   17 Dec 2026". The last one starting on or before the chosen date, so a bound
	   that falls on a day the pattern misses still names the instance the set
	   really ends after rather than reading as a bare calendar date nothing
	   happens on. */
	const lastSurviving = endsInstances.reduce<EndsInstance | null>(
		(latest, instance) =>
			instance.startDate && instance.startDate <= boundEndDate
				? latest === null || (latest.startDate ?? '') < instance.startDate
					? instance
					: latest
				: latest,
		null
	);

	const describeInstance = (instance: EndsInstance): string =>
		formatMessage(
			{id: 'task_set.ends.after_instance'},
			{companyTaskId: instance.companyTaskId ?? '', date: formatSetDate(intl, instance.startDate)}
		);

	/* One option per instance the field can still reach, each naming what picking
	   it costs. The list stops at the floor: below that an option would destroy an
	   instance this viewer may not delete, so it is not offered rather than
	   offered and refused.

	   A set that cannot be shortened at all reports NO floor -- there is no
	   reachable instance to name one with -- and that null must not read as "no
	   limit". Left unguarded it offered the whole series, every option of which the
	   server refuses. The last instance is the only one left to show, and choosing
	   it changes nothing. */
	const floorSequence = cannotShorten
		? endsInstances[endsInstances.length - 1]?.sequence ?? 0
		: endsPreflight?.floorSequence ?? 0;
	const endsOptions = endsInstances
		.filter(instance => instance.sequence != null && instance.sequence >= floorSequence)
		.map(instance => {
			const removed = endsInstances.filter(
				other => other.startDate && instance.startDate && other.startDate > instance.startDate
			).length;
			return {
				id: String(instance.sequence),
				startDate: instance.startDate ?? '',
				name: describeInstance(instance),
				removed,
			};
		});

	/* What the field shows until the preflight arrives.

	   The preflight is a second request, made when the form mounts, and the
	   options cannot be built without it. Selecting nothing meanwhile left Ends
	   blank for a beat on every open -- and since the form is now rebuilt each
	   time a set is opened, that beat happens every time rather than once.

	   The set already carries the instance it ends after, loaded with everything
	   else, so the field can say so immediately. One option, already selected,
	   replaced by the real list the moment it lands. */
	const pendingEnd =
		endsPreflight || set.lastInstance?.companyTaskId == null || !set.lastInstance?.startDate
			? null
			: {
					id: PENDING,
					startDate: set.lastInstance.startDate,
					name: formatMessage(
						{id: 'task_set.ends.after_instance'},
						{
							companyTaskId: set.lastInstance.companyTaskId,
							date: formatSetDate(intl, set.lastInstance.startDate),
						}
					),
					removed: 0,
			  };

	/* The set's own rule, in the shape the shared expander reads. Used to count
	   what a later date adds and to find where the "Later date" option opens --
	   re-walked from the seed rather than counted off an interval from the last
	   instance, for the same reason the backend re-walks it: a monthly set clamped
	   to a short month would otherwise carry that clamp forward. */
	const patternForm: TaskSetFormState = {
		frequency: (set.frequency as TaskSetFormState['frequency']) || 'WEEKLY',
		intervalLength: set.intervalLength || 1,
		weekdays: (set.weekdays || []).filter((day): day is string => !!day),
		monthlyWeekday: set.monthlyWeekday ?? null,
		monthlyMode: (set.monthlyMode as TaskSetFormState['monthlyMode']) ?? null,
		dayOfMonth: set.dayOfMonth ?? null,
		weekOrdinal: set.weekOrdinal ?? null,
		seedDate: set.seedDate ?? '',
		boundType: 'DATE',
		boundCount: null,
		boundEndDate,
	};

	/* Where the "Later date" option lands: the next date the set's own pattern
	   produces after the last instance. Opening on the last instance's own date
	   instead would mean the option changed nothing, and opening on an arbitrary
	   offset would propose a date the rule never generates. */
	const nextDateAfterSeries = (() => {
		if (!set.seedDate || !lastStart) return boundEndDate;
		const reach = expandRecurrence({...patternForm, boundEndDate: addYear(lastStart)});
		return reach.find(date => date > lastStart) ?? lastStart;
	})();

	/* A date past the whole series names no instance, so the field says so and
	   offers a date picker instead of a list -- but only once the reader has
	   chosen Ends themselves. As the form opens, a set that is not short has
	   nothing past its last task: its stored end may fall on a day the pattern
	   skips (every Monday, ending Wednesday 31 Dec, last task Monday 29 Dec), or
	   the tasks after the last one may have been deleted, and either way it
	   names that last task rather than reading as a later date. A set that IS
	   short opens on where generation got, which is its last task too. */
	const beyondSeries = !!lastStart && boundEndDate > lastStart && endsTouched;

	// How many instances a later date would create.
	const addedCount =
		endsChanged && !shortening && set.seedDate
			? expandRecurrence(patternForm).filter(date => !lastStart || date > lastStart).length
			: 0;

	/* What the notice says. A count set is the one place the drawer shows instance
	   counts, because the count is what the reader asked for. A reader who cannot
	   edit did not necessarily ask, and cannot act, so theirs names who can. */
	const countShortfall = isCountShortfall(set);
	const shortfallNotice = shortfall
		? formatMessage(
				{
					id: countShortfall
						? readOnly
							? 'task_set.shortfall.notice_count_read_only'
							: 'task_set.shortfall.notice_count'
						: readOnly
						? 'task_set.shortfall.notice_date_read_only'
						: 'task_set.shortfall.notice_date',
				},
				{
					requestedDate: formatSetDate(intl, shortfall.requestedEndDate),
					// "The last task is on" -- the watermark generation actually reached,
					// which is what the Ends field opens on; falling back to the loaded
					// last instance only when the set carries no watermark of its own.
					// An instance dragged after it would otherwise disagree with Ends.
					lastTaskDate: formatSetDate(intl, shortfall.createdUpTo ?? set.lastInstance?.startDate),
					requested: shortfall.requestedCount,
					created: shortfall.createdCount,
					createdDate: formatSetDate(intl, shortfall.createdUpTo),
				}
		  )
		: null;
	/* Where the shortfall action takes Ends: the requested end, unless an
	   instance was dragged past it. Ending before that instance would make the
	   "extend" a shortening that deletes it, so the action never goes earlier
	   than the latest start. */
	const extendTarget =
		shortfall?.requestedEndDate && lastStart && lastStart > shortfall.requestedEndDate
			? lastStart
			: shortfall?.requestedEndDate ?? null;
	// Fills the field and nothing else: saving stays the reader's decision and
	// goes through the same Ends flow as any other extension.
	const shortfallAction = shortfall
		? countShortfall
			? formatMessage({id: 'task_set.shortfall.extend_count'}, {count: shortfall.remainingCount})
			: formatMessage({id: 'task_set.shortfall.extend_date'}, {date: formatSetDate(intl, extendTarget)})
		: null;

	const hasChanges = (changedFields.length > 0 || endsChanged) && !blankedAName && !belowFloor;

	const commit = (fromSequence: number | null) => {
		setSaving(true);
		const input: Record<string, unknown> = {
			id: set.id,
			globalProjectId: projectId,
		};
		if (fromSequence !== null) input.fromSequence = fromSequence;
		if (changedFields.includes('setName')) input.setName = setName;
		if (changedFields.includes('name')) input.instanceName = instanceName;
		// An emptied prose field is a deliberate clear, and the mutation needs a
		// value it can tell apart from "not supplied".
		if (changedFields.includes('description')) input.description = description === '' ? '__CLEAR__' : description;
		if (changedFields.includes('estimate')) {
			if (storedEstimate === null) input.clearEstimateForecast = true;
			else input.estimateForecast = storedEstimate;
		}
		if (changedFields.includes('role')) {
			if (roleId === null) input.clearRole = true;
			else input.globalRoleId = roleId;
		}
		if (changedFields.includes('phase')) {
			if (phaseId === null) input.clearPhase = true;
			else input.globalPhaseId = phaseId;
		}
		if (changedFields.includes('assigned')) input.globalAssignedPersonIds = assignedPersonIds;
		if (changedFields.includes('labels')) input.globalLabelIds = labelIds;
		if (changedFields.includes('billable')) input.billable = !nonBillable;
		// No clear sentinel: a set always ends somewhere, and emptying the field is
		// not offered.
		if (endsChanged) input.boundEndDate = boundEndDate;

		Util.CommitMutation(
			UpdateTaskSetMutation,
			input,
			(response: {updateTaskSet?: {instancesUpdated?: number}}) => {
				setSaving(false);
				const instancesUpdated = response?.updateTaskSet?.instancesUpdated ?? 0;
				// The real number, including zero. "Changes saved" over an edit that
				// reached no instance would let a reader believe the whole series
				// moved, which is the one way this flow could quietly mislead.
				/* The end date gets its own line when it moved. It is the one change
				   whose effect the propagation count does not describe -- destroying
				   and creating instances is not "instances took the change" -- so
				   folding it into that sentence would undercount visibly. */
				createToast({
					duration: 4000,
					message: endsChanged
						? formatMessage({id: 'task_set.edit.ends_updated'}, {date: formatSetDate(intl, boundEndDate)})
						: formatMessage({id: 'task_set.edit.updated'}, {count: instancesUpdated}),
				});
				// Anything that reached an instance, and every Ends move, changes the
				// list this form judges the next Ends change against.
				refetchEnds();
				onSaved(instancesUpdated);
			},
			true,
			null,
			// Without this the form stays saving for ever and Save is dead. The
			// fallback returns true when it has scheduled its own retry, meaning
			// this attempt is not over.
			(transaction: unknown, onFailure?: (t: unknown) => boolean | void) => {
				/* An extend whose new tasks could not be created rolled back whole.
				   Say so instead of the generic modal, and keep the chosen end so
				   the reader can try again. */
				if (String(transaction).includes(EXTEND_FAILED_CODE)) {
					setSaving(false);
					createToast({duration: 8000, message: formatMessage({id: 'task_set.ends.extend_failed'})});
					return;
				}
				const retryInFlight = onFailure ? onFailure(transaction) === true : false;
				if (!retryInFlight) setSaving(false);
			}
		);
	};

	/**
	 * Ask how far the change should reach, then apply it.
	 *
	 * <p>The chooser is skipped for a set nothing has touched: every instance is
	 * identical, so the question would have one answer. That decision comes from
	 * the server, not from anything this form can see.
	 */
	const askScopeOrCommit = () => {
		// Nothing that reaches an instance changed -- only Ends moved, or only the
		// set's own name. The scope chooser asks which instances take an attribute
		// change, so with none to scope it would ask a question with no bearing on
		// the outcome, and then answer it with zero.
		if (instanceChangedFields.length === 0) {
			commit(null);
			return;
		}
		setSaving(true);
		awaitingPreflight.current = true;
		fetchPreflight(
			{id: set.id},
			undefined,
			// A failed preflight must not silently apply the edit at the widest
			// scope. Releasing the button lets the reader try again.
			() => {
				// Silence here would leave Save spinning with nothing to press.
				awaitingPreflight.current = false;
				setSaving(false);
				createToast({duration: 4000, message: formatMessage({id: 'task_set.preflight_failed'})});
			}
		);
	};

	const confirmEnds = (preflight: EndsPreflight) => {
		setSaving(false);
		showTaskSetEndsModal({
			/* What the FIELD showed, not the preflight's own answer. The two differ on
			   a date-bounded set whose pattern does not land on the bound -- the
			   stored end is 31 Oct, the last instance starts 28 Oct -- and quoting the
			   preflight there would open a modal contradicting the field the reader
			   just edited. The preflight is the fallback for a set with no stored
			   end. */
			currentEndDate: initialEnd || preflight.currentEndDate || null,
			newEndDate: boundEndDate,
			instances: preflight.instances ?? [],
			isPointsProject,
			currencySymbol,
			onConfirm: askScopeOrCommit,
		});
	};

	/**
	 * Confirm the end date first, then ask how far the rest of the edit reaches.
	 *
	 * <p>In that order because the Ends confirmation is the destructive one: a
	 * reader who backs out of deleting six instances should not have already
	 * answered a question about propagating a rename.
	 *
	 * <p>The ends preflight is normally already in hand -- it is fetched when the
	 * field is first touched -- and refetched here only if it is not, which is the
	 * race where Save lands before that round trip.
	 */
	const onSave = () => {
		if (!hasChanges || saving) return;
		/* Only a SHORTEN confirms. A later date moves no existing instance's date
		   and destroys nothing, so it passes silently -- the number it adds is
		   already on the field, and a dialog for a harmless change teaches people
		   to click through the one that is not. */
		if (!shortening || !endsPreflight) {
			askScopeOrCommit();
			return;
		}
		confirmEnds(endsPreflight);
	};

	// Driven off the fetched data rather than the fetch callback, which is not
	// handed the result.
	const preflight = preflightData?.node?.editPreflight;
	useEffect(() => {
		if (!awaitingPreflight.current || !preflight) return;
		awaitingPreflight.current = false;
		// A set nothing has touched needs no chooser: every instance is identical,
		// so the question would have one answer. That call belongs to the server,
		// not to anything this form can see.
		if (preflight.untouched) {
			commit(null);
			return;
		}
		setSaving(false);
		showTaskSetScopeModal({
			changedFields: instanceChangedFields,
			instances: preflight.instances || [],
			onApply: commit,
		});
		// Keyed on the preflight alone. commit and the changed-field lists are
		// rebuilt every render, so listing them would re-run this on every
		// keystroke; the values captured here are the ones current when the
		// preflight landed, which is what the reader pressed Save on.
	}, [preflight]);

	return (
		<FormRoot data-cy="task-set-edit-form">
			{/* What Save will do. Nothing will be saved in read-only, so the line
			    would describe an action the reader cannot take. */}
			{readOnly ? null : <Notice>{formatMessage({id: 'task_set.edit.notice'})}</Notice>}

			<Row>
				{/* Set name first, matching the design: the series is named before the
				    work it produces. Both are here because they are different
				    strings -- the set is named for the series, each instance for the
				    work. */}
				<TextField
					label={formatMessage({id: 'task_set.set_name'})}
					value={setName}
					onValueChange={setSetName}
					maxLength={NAME_MAX_LENGTH}
					width="flex"
					disabled={readOnly}
					data-cy="task-set-edit-set-name"
				/>
				<TextField
					label={formatMessage({id: 'task_set.instance_name'})}
					value={instanceName}
					onValueChange={setInstanceName}
					maxLength={NAME_MAX_LENGTH}
					width="flex"
					disabled={readOnly}
					data-cy="task-set-edit-instance-name"
				/>
			</Row>

			<Row>
				<TextArea
					label={formatMessage({id: 'common.description'})}
					value={description}
					isOptional
					height={76}
					onChange={(value: string) => setDescription(value)}
					onBlur={() => undefined}
					disabled={readOnly}
					data-cy="task-set-edit-description"
				/>
			</Row>

			<Row>
				{isPointsProject ? (
					<TextField
						label={formatMessage({id: 'task_set.edit.estimate_per_instance'})}
						value={estimateValue == null ? '' : String(estimateValue)}
						onValueChange={(value: string) => {
							const parsed = Number(value);
							setEstimateValue(value === '' || !Number.isFinite(parsed) ? null : parsed);
						}}
						width="flex"
						disabled={readOnly}
						data-cy="task-set-edit-estimate-points"
					/>
				) : (
					<Field>
						<Label>{formatMessage({id: 'task_set.edit.estimate_per_instance'})}</Label>
						<HoursInput
							replicateDesignSystem
							width="100%"
							value={estimateValue ?? 0}
							mutation={(value: unknown) => {
								if (typeof value === 'number' && Number.isFinite(value)) {
									setEstimateValue(value > 0 ? value : null);
								} else if (value === null || value === undefined || value === '') {
									setEstimateValue(null);
								}
							}}
							disabled={readOnly}
							cy="task-set-edit-estimate-hours"
						/>
					</Field>
				)}
				<AssignedField>
					<Label>{formatMessage({id: 'common.role'})}</Label>
					<AssignedDropdown
						assignableRoles={roles
							.filter(role => role.name)
							.map(role => ({node: {id: role.id, name: role.name as string}}))}
						assignedRole={roles.find(role => role.id === roleId) ?? null}
						assignRole={(value: string | null) => setRoleId(value || null)}
						isMultiSelect={false}
						showSuggestions={false}
						disabled={readOnly}
						closeOnChoice={true}
						cy="task-set-edit-role"
					/>
				</AssignedField>
			</Row>

			{/* Assignees and Phase share a row, as drawn. On separate rows each
			    stretched to the full width of the drawer, which read as a column of
			    slabs rather than a form -- and put two controls at a width nothing
			    else in the form uses. A project with no phases leaves Assignees
			    alone in the row, where it fills the width on its own. */}
			<Row>
				<AssignedField>
					<Label>{formatMessage({id: 'common.assignees'})}</Label>
					<AssignedDropdown
						assignablePersons={assignablePersons.map(person => ({node: {person}}))}
						assignedPersons={assignablePersons.filter(person => assignedPersonIds.includes(person.id))}
						assignPerson={(personIds: string[]) => setAssignedPersonIds(personIds)}
						unassignPerson={(personId: string) =>
							setAssignedPersonIds(assignedPersonIds.filter(id => id !== personId))
						}
						isMultiSelect={true}
						showSuggestions={false}
						disabled={readOnly}
						cy="task-set-edit-assignee"
					/>
				</AssignedField>
				{phases.length === 0 ? null : (
					<Field>
						<Label>{formatMessage({id: 'task_set.phase_label'})}</Label>
						{/* The design system's own control, like every other picker in
						    this form. A bare <select> renders as the browser's widget --
						    a different height, border and caret from the Role and Ends
						    controls it sits beside. */}
						<ComboBox
							aria-label={formatMessage({id: 'task_set.phase_label'})}
							defaultItems={[
								{id: '', name: formatMessage({id: 'task_set.none'})},
								...phases.map(phase => ({id: phase.id, name: phase.name ?? ''})),
							]}
							value={phaseId ?? ''}
							onChange={(value: string) => setPhaseId(value || null)}
							width="flex"
							disabled={readOnly}
							data-cy="task-set-edit-phase"
						>
							{(item: {id: string; name: string}) => (
								<ComboBox.Item textValue={item.name}>
									<Text noWrap>{item.name}</Text>
								</ComboBox.Item>
							)}
						</ComboBox>
						{/* The split is deliberate, so the form says so rather than
						    letting it surprise someone after the fact. */}
						<Hint>{formatMessage({id: 'task_set.edit.phase_note'})}</Hint>
					</Field>
				)}
			</Row>

			<Row>
				<Field>
					<Label>{formatMessage({id: 'common.labels'})}</Label>
					{/* The chips on their own in read-only, never the picker switched
					    off. The design system's MultiCombobox forwards `disabled` to
					    its wrapper but not to the text input underneath, so a disabled
					    one still takes focus and keystrokes -- a field that accepts
					    typing in a view-only form. Showing what is set, and nothing to
					    type into, is also what the field is FOR here. */}
					{readOnly ? (
						<ReadOnlyLabels data-cy="task-set-edit-labels-readonly">
							{selectedLabels.length === 0 ? (
								<Text color="secondary">{formatMessage({id: 'task_set.none'})}</Text>
							) : (
								<FlexRow gap={'xs'} wrap={'wrap'}>
									{selectedLabels.map(label => (
										<WorkSignifier key={label.id} workId={label.name || ''} color={label.color || ''} />
									))}
								</FlexRow>
							)}
						</ReadOnlyLabels>
					) : (
						<LabelDropdown
							height="large"
							labels={labels.slice() as never}
							selectedItems={labelIds}
							setSelectedItems={setLabelIds}
							data-cy="task-set-edit-labels"
						/>
					)}
				</Field>
			</Row>

			{showBillable ? (
				<Row>
					<Checkbox
						checked={nonBillable}
						onChange={checked => setNonBillable(checked === true)}
						labelText={formatMessage({id: 'task_set.non_billable'})}
						notBoldLabel
						disabled={readOnly}
						aria-label={formatMessage({id: 'task_set.non_billable'})}
						data-cy="task-set-edit-non-billable"
					/>
				</Row>
			) : null}

			{/* Pattern and Ends sit side by side, as drawn: they are the two fields
			    that describe how the set RUNS, as against what it stamps, and the
			    disabled one reads as deliberate beside a live one rather than as a
			    dead row of its own. */}
			<Row>
				<Field data-cy="task-set-edit-pattern-locked">
					<Label>{formatMessage({id: 'task_set.pattern'})}</Label>
					<LockedValue>{describePattern(intl, set) ?? formatMessage({id: 'task_set.none'})}</LockedValue>
					<Hint>{formatMessage({id: 'task_set.edit.pattern_locked'})}</Hint>
				</Field>
				<Field>
					<Label>{formatMessage({id: 'task_set.ends'})}</Label>
					{/* An instance picker, not a date picker. The set ends AFTER a
					    particular occurrence, and naming it -- "After T707, 17 Dec 2026"
					    -- is what makes the choice legible: a bare date leaves the
					    reader working out for themselves which work it keeps. Dates
					    past the last instance name nothing, so those get their own
					    option and their own picker. */}
					<ComboBox
						aria-label={formatMessage({id: 'task_set.ends'})}
						defaultItems={
							pendingEnd
								? [pendingEnd]
								: [
										...endsOptions,
										{
											id: LATER,
											startDate: '',
											name: formatMessage({id: 'task_set.ends.later_date'}),
											removed: 0,
										},
								  ]
						}
						value={pendingEnd ? PENDING : beyondSeries ? LATER : String(lastSurviving?.sequence ?? '')}
						onChange={(value: string) => {
							setEndsTouched(true);
							if (value === LATER) {
								// Straight past the series, so the picker below opens on a
								// date that is already an extension rather than one that
								// would silently delete something.
								setBoundEndDate(nextDateAfterSeries);
								return;
							}
							const picked = endsOptions.find(option => option.id === value);
							if (picked) setBoundEndDate(picked.startDate);
						}}
						width="flex"
						disabled={readOnly}
						data-cy="task-set-edit-ends"
					>
						{(item: {id: string; name: string; removed: number}) => (
							<ComboBox.Item textValue={item.name}>
								<Text noWrap>
									{item.removed > 0
										? `${item.name} — ${formatMessage(
												{id: 'task_set.ends.option_removes'},
												{count: item.removed}
										  )}`
										: item.name}
								</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
					{/* Only for a date past the last instance, which names no occurrence
					    and so cannot be chosen from the list above. */}
					{beyondSeries ? (
						/* Floored at the day after the last instance, because this picker
						   is reached through an option that says LATER date. Without the
						   floor it took any date at all: one inside the series silently
						   became a shortening, and one before the first instance emptied
						   the field and killed Save with nothing on screen saying why.
						   Shortening has its own control -- the instance options above. */
						<DatePicker
							label={formatMessage({id: 'task_set.ends_on_date'})}
							value={boundEndDate}
							minValue={lastStart ? nextDay(lastStart) : undefined}
							onChange={(value: string) => {
								setEndsTouched(true);
								setBoundEndDate(value);
							}}
							disabled={readOnly}
							data-cy="task-set-edit-ends-date"
						/>
					) : null}
					{/* One line, and the order matters. What the reader is DOING wins: once
					    they have chosen a later date, how many instances that adds is the
					    only thing they still need, and repeating that the set cannot end
					    earlier answers a question they have stopped asking -- while hiding
					    the number they have not got. The refusal comes next, because it
					    governs the list they are choosing from. The standing explanation
					    is last. */}
					{beyondSeries && endsChanged ? (
						<Hint data-cy="task-set-edit-ends-adds">
							{formatMessage({id: 'task_set.ends.adds'}, {count: addedCount})}
						</Hint>
					) : shortfall ? (
						<>
							<ShortfallNotice data-cy="task-set-edit-shortfall">
								<ShortfallNoticeText>
									<Icon icon="warning" size="s" color="warning" aria-hidden="true" />
									<Text>{shortfallNotice}</Text>
								</ShortfallNoticeText>
								{readOnly ? null : (
									<Button
										emphasis={EMPHASIS.LOW}
										disabled={!endsPreflight || !extendTarget}
										// Fills the field and nothing else, exactly like picking an option
										// from the Ends list above -- saving is still a separate step.
										onPress={() => {
											setEndsTouched(true);
											extendTarget && setBoundEndDate(extendTarget);
										}}
										data-cy="task-set-edit-shortfall-extend"
									>
										{shortfallAction}
									</Button>
								)}
							</ShortfallNotice>
							{/* The notice replaces only the standing hint -- a set that also
							    cannot be shortened still needs that said, just underneath. */}
							{cannotShorten ? (
								<Hint data-cy="task-set-edit-ends-blocked">{formatMessage({id: 'task_set.ends.blocked'})}</Hint>
							) : null}
						</>
					) : cannotShorten ? (
						<Hint data-cy="task-set-edit-ends-blocked">{formatMessage({id: 'task_set.ends.blocked'})}</Hint>
					) : (
						<Hint>{formatMessage({id: 'task_set.ends.hint'})}</Hint>
					)}
				</Field>
			</Row>

			<Actions>
				{/* waiting, not just disabled: an edit can rewrite many instances, so
				    the wait is visible. The design system swaps the label for a loader
				    rather than leaving the only feedback a greyed-out word.

				    Omitted entirely in read-only: a permanently greyed Save says this
				    particular set cannot be saved, which is a different thing from
				    not being allowed to save anything. */}
				{readOnly ? null : (
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={onSave}
						disabled={!hasChanges}
						waiting={saving}
						data-cy="task-set-edit-save"
					>
						{formatMessage({id: 'task_set.edit.save'})}
					</Button>
				)}
				{/* Cancel abandons an edit; with nothing to abandon the same button is
				    just the way out. */}
				<Button emphasis={EMPHASIS.MEDIUM} onPress={onCancel} data-cy="task-set-edit-cancel">
					{formatMessage({id: readOnly ? 'common.close' : 'common.cancel'})}
				</Button>
			</Actions>
		</FormRoot>
	);
};

export default TaskSetEditForm;
